<template>
  <div class="space-y-8">
    <DraftBanner v-if="hasDraft && draftSavedAt !== null" :saved-at="draftSavedAt" @restore="onRestoreDraft" @discard="discardDraft" />
    <!-- Company profile: the name is prefilled from checkout (master tenants.name)
         but editable and REQUIRED — it may be blank for tenants created without one,
         and People's company-profile write rejects an empty name. The basics are
         optional and staged into People's company profile. The headquarters
         address is not asked here: it comes with the owner's own name, in the
         details step (#1027). -->
    <section class="space-y-4">
      <h3 class="text-base font-semibold text-heading">{{ t('onboarding.wizard.general.company.title') }}</h3>
      <Field :label="`${t('onboarding.wizard.general.name.label')} *`" :hint="t('onboarding.wizard.general.name.hint')">
        <Input v-model="companyName" data-test="wizard-company-name" :invalid="showNameError" />
        <p v-if="showNameError" class="mt-1 text-xs text-danger-text">{{ t('onboarding.wizard.general.name.required') }}</p>
      </Field>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field :label="t('onboarding.wizard.general.profile.industry')"><Input v-model="profile.industry" /></Field>
        <Field :label="t('onboarding.wizard.general.profile.employeeCountRange')">
          <Input v-model="profile.employeeCountRange" :placeholder="t('onboarding.wizard.general.profile.employeeCountPlaceholder')" />
        </Field>
        <Field :label="t('onboarding.wizard.general.profile.foundedYear')">
          <Input v-model="profile.foundedYear" type="number" :placeholder="t('onboarding.wizard.general.profile.foundedYearPlaceholder')" />
        </Field>
        <Field :label="t('onboarding.wizard.general.profile.legalForm')"><Input v-model="profile.legalForm" /></Field>
        <div class="md:col-span-2"><Field :label="t('onboarding.wizard.general.profile.websiteUrl')"><Input v-model="profile.websiteUrl" /></Field></div>
      </div>
    </section>

    <!-- Org structure — staged ➕-lists per unit type. Add + remove are pure
         client-side list ops; nothing is written to People until Finish. -->
    <section class="space-y-4">
      <div>
        <h3 class="text-base font-semibold text-heading flex items-center gap-1">
          {{ t('onboarding.wizard.general.org.title') }}
          <CrossRefHint :modules="[t('onboarding.modules.ats'), t('onboarding.modules.apprenticeship'), t('onboarding.modules.time_tracking')]" />
        </h3>
        <p class="text-sm text-body">{{ t('onboarding.wizard.general.org.subtitle') }}</p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div v-for="type in ORG_TYPES" :key="type">
          <p class="text-sm font-medium text-heading mb-2">{{ t(`onboarding.wizard.general.org.${ORG_LABEL_KEY[type]}`) }}</p>
          <!-- Locations are the one unit type that carries an optional real
               place (D13), so they get their own list editor. -->
          <WizardLocationInput
            v-if="type === 'location'"
            ref="orgInputs"
            v-model="locations"
            :placeholder="t('onboarding.wizard.general.org.addLocation')"
            :empty-text="t('onboarding.wizard.general.org.empty')"
          />
          <WizardListInput
            v-else
            ref="orgInputs"
            v-model="orgLists[type]"
            :placeholder="t(`onboarding.wizard.general.org.${ORG_ADD_KEY[type]}`)"
            :empty-text="t('onboarding.wizard.general.org.empty')"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Field, Input } from '@lumi/ui';
import { DraftBanner, useApiMutation, useFormDraft } from '@lumi/app-kit';
import { userApi } from '../../utils/api';
import { useOnboardingStore, type OnboardingStatus } from '../../stores/onboarding';
import { useAuthStore } from '../../stores/auth';
import { useTenantsStore } from '../../stores/tenants';
import type { StagedLocation } from '../../utils/places';
import CrossRefHint from './CrossRefHint.vue';
import WizardListInput from './WizardListInput.vue';
import WizardLocationInput from './WizardLocationInput.vue';

const { t } = useI18n();
const onboardingStore = useOnboardingStore();
const authStore = useAuthStore();
const tenantsStore = useTenantsStore();

// Teams are not asked for at setup (#546). People still has the unit type, and
// the replay still writes one (onboarding-replay.ts) — the wizard just does not
// offer it.
type OrgType = 'department' | 'location' | 'cost_center';
const ORG_TYPES: OrgType[] = ['department', 'location', 'cost_center'];
const ORG_LABEL_KEY: Record<OrgType, string> = {
  department: 'departments',
  location: 'locations',
  cost_center: 'costCenters',
};
const ORG_ADD_KEY: Record<OrgType, string> = {
  department: 'addDepartment',
  location: 'addLocation',
  cost_center: 'addCostCenter',
};

// The plain text fields.
const PROFILE_FIELDS = [
  'industry',
  'employeeCountRange',
  'foundedYear',
  'legalForm',
  'websiteUrl',
] as const;

// The headquarters is the details step's (#1027). While a demo is live that step
// stages it into this very blob (the Finish wipe would erase a direct write), so
// saving here must carry it through rather than replace the blob without it.
const HQ_KEYS = ['hqStreet', 'hqPostalCode', 'hqCity', 'hqCountry', 'hqCountryCode'] as const;

// Editable + required; prefilled from a prior staged edit or the checkout name.
const companyName = ref('');
const showNameError = ref(false);
// Values are strings, except what a `type="number"` input writes back: Vue's
// v-model casts it to a number (issue 561), so read them through String().
const profile = reactive<Record<string, string | number>>({});
const orgLists = reactive<Record<OrgType, string[]>>({ department: [], location: [], cost_center: [] });
// Locations live beside the other lists rather than in them: their staged shape
// is an object, and the editor beside them has a town search of its own.
const locations = ref<StagedLocation[]>([]);
// The org-unit editors, so leaving the section can keep what was typed into one
// but never added (#546).
const orgInputs = ref<Array<{ flush: () => void }>>([]);

onMounted(() => {
  // Resume from the staged blob — nothing is read from People (staged model).
  const staged = onboardingStore.staged['general'] || {};
  companyName.value = String((staged as any).companyName ?? onboardingStore.companyName ?? '');
  for (const key of PROFILE_FIELDS) {
    const v = (staged as any)[key];
    if (v !== undefined && v !== null) profile[key] = String(v);
  }
  const units = Array.isArray((staged as any).orgUnits) ? (staged as any).orgUnits : [];
  for (const type of ORG_TYPES) orgLists[type] = [];
  locations.value = [];
  for (const u of units) {
    // A team staged before teams left the wizard (#546) is not re-staged: nothing
    // here could show it, and turning it into a department would invent one.
    if (u?.type === 'team') continue;
    const type = (ORG_TYPES as string[]).includes(u?.type) ? (u.type as OrgType) : 'department';
    const name = String(u?.name || '').trim();
    if (!name) continue;
    if (type === 'location') locations.value.push({ name });
    else orgLists[type].push(name);
  }
  // Server-side staging only captures a snapshot when the wizard advances past
  // this section (persistComplete → stageAnswers). Everything typed between
  // that and a reload/crash lived nowhere until this — this is the "form
  // repopulated" point the draft re-primes from.
  noteFormReset();
});

// The wizard's own staged blob already survives a reload once the section is
// completed; this draft covers the gap before that — typing in progress that
// was never staged. tenantsStore.activeTenant is resolved by AppChrome's
// session bootstrap before this section can render (it lives behind the
// authenticated workspace, never the bare/login route).
const {
  hasDraft,
  draftSavedAt,
  restore: restoreDraft,
  discard: discardDraft,
  noteFormReset,
  clearOnSave: clearDraftOnSave,
} = useFormDraft({
  tenantSlug: () => tenantsStore.activeTenant?.slug ?? null,
  userId: () => authStore.user?.userId ?? null,
  formId: 'workspace.wizardGeneral',
  recordId: () => 'general',
  values: () => ({
    companyName: companyName.value,
    profile: JSON.parse(JSON.stringify(profile)),
    orgLists: JSON.parse(JSON.stringify(orgLists)),
    locations: JSON.parse(JSON.stringify(locations.value)),
  }),
});

function onRestoreDraft() {
  restoreDraft((values) => {
    const v = values as unknown as {
      companyName?: string;
      profile?: Record<string, string>;
      orgLists?: Record<OrgType, string[]>;
      locations?: StagedLocation[];
    };
    if (v.companyName !== undefined) companyName.value = v.companyName;
    if (v.profile) Object.assign(profile, v.profile);
    if (v.orgLists) for (const type of ORG_TYPES) orgLists[type] = v.orgLists[type] ?? [];
    if (v.locations) locations.value = v.locations;
  });
}

// inlineEdit + the default `global` channel: a successful save is confirmed by
// the wizard advancing to the next section, and a failure has nowhere in this
// section to render — so it takes the channel that cannot be silent, and
// `persistComplete` returns false to keep the user on the section they were on.
const { mutate: stageAnswers } = useApiMutation<void, [string, Record<string, any>]>({
  tier: 'inlineEdit',
  run: async (key, payload) => {
    const tenantId = await onboardingStore.activeTenantId();
    if (!tenantId) return;
    const res = await userApi.put(
      `${onboardingStore.base(tenantId)}/staged/${encodeURIComponent(key)}`,
      payload,
    );
    onboardingStore.apply(res.data as OnboardingStatus);
  },
});

// Stage the section (ADR 0032): no module write. The company name is REQUIRED —
// veto (stay on the section) if it's blank, since the replay's company-profile
// write rejects an empty name. Everything else is optional.
async function persistComplete(): Promise<boolean> {
  for (const input of orgInputs.value) input.flush();
  const name = companyName.value.trim();
  if (!name) {
    showNameError.value = true;
    return false;
  }
  showNameError.value = false;
  const payload: Record<string, any> = { companyName: name };
  for (const key of PROFILE_FIELDS) {
    const v = String(profile[key] ?? '').trim();
    if (v) payload[key] = v;
  }
  const prior = onboardingStore.staged['general'];
  for (const key of HQ_KEYS) {
    const v = (prior as any)?.[key];
    if (typeof v === 'string' && v.trim()) payload[key] = v;
  }

  const orgUnits: Array<Record<string, unknown>> = [];
  for (const type of ORG_TYPES) {
    if (type === 'location') continue;
    for (const name of orgLists[type]) orgUnits.push({ type, name });
  }
  for (const location of locations.value) {
    orgUnits.push({ type: 'location', name: location.name });
  }
  if (orgUnits.length) payload.orgUnits = orgUnits;

  const hadStaged = !!prior && (Object.keys(prior).length > 0);
  if (Object.keys(payload).length > 0 || hadStaged) {
    const outcome = await stageAnswers('general', payload);
    if (!outcome.ok) return false;
  }
  clearDraftOnSave();
  return true;
}

defineExpose({ persistComplete });
</script>
